import React from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import type {
  AdminProductSearchItem,
  InventoryAddRowDraft,
  ProductConditionOption,
  ProductSizeOption,
} from '../types/adminInventoryAdd';
import { formatMoneyDisplay, parseMoneyInputToNumber } from '../utils/moneyDisplay';
import InventoryCommentsSelect from './InventoryCommentsSelect';

const ACCENT = '#3B82F6';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: 'rgba(248, 250, 252, 1)',
    '& fieldset': { border: 'none' },
    '&.Mui-focused': { boxShadow: `0 0 0 2px ${ACCENT}` },
  },
};

const productAutocompleteSlotProps = {
  paper: {
    elevation: 0,
    sx: {
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 16px 48px rgba(15, 23, 42, 0.12)',
      mt: 0.5,
      overflow: 'hidden',
    },
  },
  listbox: {
    sx: {
      py: 0.75,
      px: 0.5,
      maxHeight: 320,
      '& .MuiAutocomplete-option': {
        borderRadius: 1.25,
        my: 0.125,
      },
    },
  },
  popper: {
    sx: { minWidth: 320 },
  },
} as const;

const tableOptionAutocompleteSlotProps = {
  paper: {
    elevation: 0,
    sx: {
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 16px 48px rgba(15, 23, 42, 0.12)',
      mt: 0.5,
    },
  },
  listbox: {
    sx: {
      py: 0.5,
      maxHeight: 280,
      '& .MuiAutocomplete-option': { borderRadius: 1, my: 0.125 },
    },
  },
  popper: {
    sx: { zIndex: 1400 },
  },
} as const;

function productSearchLabel(option: AdminProductSearchItem): string {
  const meta = [option.brand, option.category].filter(Boolean).join(' · ');
  return meta ? `${option.title} (${meta})` : option.title;
}

interface CreateProductOption {
  kind: 'create';
  id: '__create__';
  title: string;
  query: string;
}

type ProductAutocompleteOption = AdminProductSearchItem | CreateProductOption;

function isCreateProductOption(
  option: ProductAutocompleteOption | null | undefined
): option is CreateProductOption {
  return Boolean(option && 'kind' in option && option.kind === 'create');
}

interface CreateSizeOption {
  kind: 'createSize';
  id: '__create_size__';
  label: string;
  query: string;
}

type SizeAutocompleteOption = ProductSizeOption | CreateSizeOption;

function isCreateSizeOption(
  option: SizeAutocompleteOption | null | undefined
): option is CreateSizeOption {
  return Boolean(option && 'kind' in option && option.kind === 'createSize');
}

function filterSizeAutocompleteOptions(
  opts: SizeAutocompleteOption[],
  inputValue: string,
  canCreate: boolean
): SizeAutocompleteOption[] {
  const q = inputValue.trim();
  const filtered = q
    ? opts.filter((o) => !isCreateSizeOption(o) && o.label.toLowerCase().includes(q.toLowerCase()))
    : opts.filter((o) => !isCreateSizeOption(o));
  if (canCreate) {
    filtered.push({
      kind: 'createSize',
      id: '__create_size__',
      label: '+ Nueva talla',
      query: q,
    });
  }
  return filtered;
}

interface CreateConditionOption {
  kind: 'createCondition';
  id: '__create_condition__';
  label: string;
  query: string;
}

type ConditionAutocompleteOption = ProductConditionOption | CreateConditionOption;

function isCreateConditionOption(
  option: ConditionAutocompleteOption | null | undefined
): option is CreateConditionOption {
  return Boolean(option && 'kind' in option && option.kind === 'createCondition');
}

function sizeAutocompleteNoOptionsText(loadingDeps: boolean, productId: number | null): string {
  if (loadingDeps) return 'Cargando…';
  if (productId) return 'Sin tallas';
  return 'Elige un producto';
}

function conditionAutocompleteNoOptionsText(
  loadingDeps: boolean,
  productId: number | null
): string {
  if (loadingDeps) return 'Cargando…';
  if (productId) return 'Sin condiciones';
  return 'Elige un producto';
}

function renderOptionInput(
  params: AutocompleteRenderInputParams,
  placeholder: string
): React.ReactElement {
  return (
    <TextField
      id={params.id}
      disabled={params.disabled}
      fullWidth
      size={params.size}
      placeholder={placeholder}
      sx={fieldSx}
      slotProps={{
        inputLabel: params.InputLabelProps,
        htmlInput: params.inputProps,
        input: {
          ref: params.InputProps.ref,
          className: params.InputProps.className,
          startAdornment: params.InputProps.startAdornment,
          endAdornment: params.InputProps.endAdornment,
        },
      }}
    />
  );
}

const LABEL_ROW_HEIGHT = 20;
const LABEL_GAP_PX = 6;

function FieldLabel({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: 'block',
        height: LABEL_ROW_HEIGHT,
        mb: `${LABEL_GAP_PX}px`,
        lineHeight: `${LABEL_ROW_HEIGHT}px`,
        fontWeight: 600,
        letterSpacing: '0.01em',
        textAlign: 'left',
      }}
    >
      {children}
    </Typography>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Box sx={{ minWidth: 0, textAlign: 'left' }}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </Box>
  );
}

export interface InventoryAddRowEditorProps {
  row: InventoryAddRowDraft;
  index: number;
  layout: 'desktop' | 'mobile';
  canEdit: boolean;
  canRemove: boolean;
  onUpdate: (patch: Partial<InventoryAddRowDraft>) => void;
  onSelectProduct: (product: AdminProductSearchItem | null) => void;
  onSearchProduct: (query: string) => void;
  onOpenCreateProduct: () => void;
  onOpenCreateSize: (query: string) => void;
  onOpenCreateCondition: (name: string) => void;
  onCostChange: (value: string) => void;
  onToggleAuto: (checked: boolean) => void;
  onRemove: () => void;
}

function InventoryAddRowEditor({
  row,
  index,
  layout,
  canEdit,
  canRemove,
  onUpdate,
  onSelectProduct,
  onSearchProduct,
  onOpenCreateProduct,
  onOpenCreateSize,
  onOpenCreateCondition,
  onCostChange,
  onToggleAuto,
  onRemove,
}: InventoryAddRowEditorProps): React.ReactElement {
  const productField = (
    <Autocomplete<ProductAutocompleteOption, false, false, false>
      size="small"
      options={
        row.productSearchInput.trim().length >= 2 && !row.loadingProducts
          ? [
              ...row.productOptions,
              {
                kind: 'create',
                id: '__create__',
                title: '+ Crear nuevo producto',
                query: row.productSearchInput.trim(),
              },
            ]
          : row.productOptions
      }
      filterOptions={(opts) => opts}
      value={row.selectedProduct}
      inputValue={row.productSearchInput}
      loading={row.loadingProducts}
      disabled={!canEdit}
      getOptionLabel={(o) => (isCreateProductOption(o) ? o.title : productSearchLabel(o))}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      blurOnSelect
      onInputChange={(_, v, reason) => {
        if (reason === 'input') {
          onUpdate({ productSearchInput: v });
          onSearchProduct(v);
          return;
        }
        if (reason === 'clear') {
          onUpdate({ productSearchInput: '' });
          onSelectProduct(null);
          onSearchProduct('');
        }
      }}
      onChange={(_, v) => {
        if (!v) {
          onSelectProduct(null);
          return;
        }
        if (isCreateProductOption(v)) {
          onOpenCreateProduct();
          return;
        }
        onSelectProduct(v);
      }}
      noOptionsText={
        row.productSearchInput.trim() && !row.loadingProducts
          ? 'Sin coincidencias'
          : 'Escribe para buscar'
      }
      slotProps={productAutocompleteSlotProps}
      renderOption={(optionProps, option) => {
        if (isCreateProductOption(option)) {
          return React.createElement(
            'li',
            optionProps,
            <Box sx={{ width: '100%', textAlign: 'left', py: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: ACCENT }}>
                {option.title}
              </Typography>
            </Box>
          );
        }
        const meta = [option.brand, option.category].filter(Boolean).join(' · ');
        return React.createElement(
          'li',
          optionProps,
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              minWidth: 0,
            }}
          >
            <Avatar
              variant="rounded"
              src={option.thumbnailUrl || undefined}
              alt=""
              imgProps={{ loading: 'lazy' }}
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                bgcolor: 'grey.200',
                color: 'text.secondary',
                fontSize: '1rem',
                fontWeight: 700,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {(option.title?.trim()?.[0] ?? '?').toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                {option.title}
              </Typography>
              {meta ? (
                <Typography variant="caption" color="text.secondary">
                  {meta}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          id={params.id}
          disabled={params.disabled}
          fullWidth
          size={params.size}
          multiline
          minRows={1}
          maxRows={6}
          placeholder="Buscar por nombre, marca o categoría…"
          sx={{
            ...fieldSx,
            '& .MuiOutlinedInput-root': {
              ...fieldSx['& .MuiOutlinedInput-root'],
              alignItems: 'flex-start',
            },
            '& .MuiOutlinedInput-input': {
              whiteSpace: 'pre-wrap',
              overflow: 'visible',
              textOverflow: 'clip',
              lineHeight: 1.4,
              py: '8px',
            },
          }}
          slotProps={{
            inputLabel: params.InputLabelProps,
            htmlInput: params.inputProps,
            input: {
              ref: params.InputProps.ref,
              className: params.InputProps.className,
              startAdornment: (
                <>
                  {row.selectedProduct?.thumbnailUrl ? (
                    <InputAdornment
                      position="start"
                      sx={{ mr: 0.25, ml: -0.25, mt: 0.75, alignSelf: 'flex-start' }}
                    >
                      <Avatar
                        variant="rounded"
                        src={row.selectedProduct.thumbnailUrl}
                        alt=""
                        sx={{
                          width: 30,
                          height: 30,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    </InputAdornment>
                  ) : null}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {row.loadingProducts ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );

  const sizeField = (
    <Autocomplete<SizeAutocompleteOption, false, false, false>
      size="small"
      fullWidth
      options={row.sizes}
      filterOptions={(opts, state) =>
        filterSizeAutocompleteOptions(opts, state.inputValue, Boolean(row.categoryId))
      }
      value={row.sizeId != null ? (row.sizes.find((s) => s.id === row.sizeId) ?? null) : null}
      onChange={(_, v) => {
        if (!v) {
          onUpdate({ sizeId: null });
          return;
        }
        if (isCreateSizeOption(v)) {
          onOpenCreateSize(v.query);
          return;
        }
        onUpdate({ sizeId: v.id });
      }}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      disabled={!canEdit || !row.productId || row.loadingDeps}
      blurOnSelect
      noOptionsText={sizeAutocompleteNoOptionsText(row.loadingDeps, row.productId)}
      slotProps={tableOptionAutocompleteSlotProps}
      renderOption={(optionProps, option) => {
        if (isCreateSizeOption(option)) {
          return React.createElement(
            'li',
            optionProps,
            <Typography variant="body2" sx={{ fontWeight: 600, color: ACCENT }}>
              {option.label}
            </Typography>
          );
        }
        return React.createElement('li', optionProps, option.label);
      }}
      renderInput={(params) => renderOptionInput(params, 'Talla')}
    />
  );

  const conditionField = (
    <Autocomplete<ConditionAutocompleteOption, false, false, false>
      size="small"
      fullWidth
      options={row.conditions}
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim();
        const filtered = q
          ? opts.filter(
              (o) => !isCreateConditionOption(o) && o.label.toLowerCase().includes(q.toLowerCase())
            )
          : opts.filter((o) => !isCreateConditionOption(o));
        if (q && row.categoryId) {
          filtered.push({
            kind: 'createCondition',
            id: '__create_condition__',
            label: '+ Nueva condición',
            query: q,
          });
        }
        return filtered;
      }}
      value={
        row.conditionId != null
          ? (row.conditions.find((c) => c.id === row.conditionId) ?? null)
          : null
      }
      onChange={(_, v) => {
        if (!v) {
          onUpdate({ conditionId: null });
          return;
        }
        if (isCreateConditionOption(v)) {
          onOpenCreateCondition(v.query);
          return;
        }
        onUpdate({ conditionId: v.id });
      }}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      disabled={!canEdit || !row.productId || row.loadingDeps}
      blurOnSelect
      noOptionsText={conditionAutocompleteNoOptionsText(row.loadingDeps, row.productId)}
      slotProps={tableOptionAutocompleteSlotProps}
      renderOption={(optionProps, option) => {
        if (isCreateConditionOption(option)) {
          return React.createElement(
            'li',
            optionProps,
            <Typography variant="body2" sx={{ fontWeight: 600, color: ACCENT }}>
              {option.label}
            </Typography>
          );
        }
        return React.createElement('li', optionProps, option.label);
      }}
      renderInput={(params) => renderOptionInput(params, 'Condición')}
    />
  );

  const costField = (
    <TextField
      size="small"
      type="text"
      inputMode="decimal"
      placeholder="Ej. 500.00"
      value={row.cost}
      onChange={(e) => onCostChange(e.target.value)}
      onBlur={(e) => {
        const t = e.target.value.trim();
        if (t === '') return;
        const n = parseMoneyInputToNumber(e.target.value);
        if (!Number.isFinite(n)) return;
        onUpdate({ cost: formatMoneyDisplay(n) });
      }}
      disabled={!canEdit}
      fullWidth
      sx={fieldSx}
    />
  );

  const publicPriceField = (
    <TextField
      size="small"
      type="text"
      inputMode="decimal"
      placeholder="Ej. 899.00"
      value={row.publicPrice}
      onChange={(e) =>
        onUpdate({
          publicPrice: e.target.value,
          autoPublicPrice: false,
        })
      }
      onBlur={(e) => {
        if (row.autoPublicPrice) return;
        const t = e.target.value.trim();
        if (t === '') return;
        const v = parseMoneyInputToNumber(e.target.value);
        if (!Number.isFinite(v)) return;
        onUpdate({ publicPrice: formatMoneyDisplay(v) });
      }}
      disabled={!canEdit || row.autoPublicPrice}
      fullWidth
      sx={{
        ...fieldSx,
        '& .MuiOutlinedInput-input': {
          fontWeight: 600,
          color: ACCENT,
        },
      }}
    />
  );

  const autoSwitch = (placement: 'start' | 'top') => (
    <FormControlLabel
      control={
        <Switch
          size="medium"
          checked={row.autoPublicPrice}
          onChange={(_, c) => onToggleAuto(c)}
          disabled={!canEdit}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: ACCENT,
            },
          }}
        />
      }
      label="Auto"
      labelPlacement={placement}
      sx={{
        m: 0,
        gap: placement === 'top' ? 0 : 0.5,
        alignItems: 'center',
        '& .MuiFormControlLabel-label': { fontSize: 12, fontWeight: 600, lineHeight: 1.2 },
      }}
    />
  );

  const commentsField = (
    <InventoryCommentsSelect
      value={row.comments}
      onChange={(comments) => onUpdate({ comments })}
      disabled={!canEdit}
    />
  );

  const removeButton = (
    <IconButton
      aria-label="Eliminar fila"
      onClick={onRemove}
      disabled={!canRemove}
      size="small"
      sx={{
        color: '#DC2626',
        '&:hover': { color: '#B91C1C', bgcolor: 'rgba(220, 38, 38, 0.08)' },
        '&.Mui-disabled': { color: 'rgba(220, 38, 38, 0.35)' },
      }}
    >
      <DeleteIcon />
    </IconButton>
  );

  if (layout === 'mobile') {
    return (
      <Box
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Artículo {index + 1}
          </Typography>
          {removeButton}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Field label="Producto">{productField}</Field>
          <Field label="Talla">{sizeField}</Field>
          <Field label="Condición">{conditionField}</Field>
          <Field label="Pago consignador">{costField}</Field>
          <Field label="Precio público">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>{publicPriceField}</Box>
              {autoSwitch('start')}
            </Box>
          </Field>
          <Field label="Comentarios">{commentsField}</Field>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '36px minmax(0,1.1fr) minmax(200px,1.05fr) minmax(0,1.3fr) 64px',
        gridTemplateAreas: `
          "index product specs comments actionsTop"
          "index product specs money    actionsBot"
        `,
        columnGap: 2,
        rowGap: 1.75,
        alignItems: 'start',
        justifyItems: 'stretch',
        px: 2,
        py: 2,
        mb: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          gridArea: 'index',
          pt: `${LABEL_ROW_HEIGHT + LABEL_GAP_PX}px`,
          color: 'text.secondary',
          fontSize: 13,
          fontWeight: 700,
          lineHeight: '40px',
          textAlign: 'left',
        }}
      >
        {index + 1}
      </Box>
      <Box sx={{ gridArea: 'product', minWidth: 0 }}>
        <Field label="Producto">{productField}</Field>
      </Box>
      <Box
        sx={{
          gridArea: 'specs',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
          minWidth: 0,
        }}
      >
        <Field label="Talla">{sizeField}</Field>
        <Field label="Condición">{conditionField}</Field>
      </Box>
      <Box sx={{ gridArea: 'comments', minWidth: 0 }}>
        <Field label="Comentarios">{commentsField}</Field>
      </Box>
      <Box
        sx={{
          gridArea: 'money',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: 2,
          minWidth: 0,
        }}
      >
        <Field label="Pago consignador">{costField}</Field>
        <Field label="Precio público">{publicPriceField}</Field>
      </Box>
      <Box
        sx={{
          gridArea: 'actionsTop',
          pt: `${LABEL_ROW_HEIGHT + LABEL_GAP_PX}px`,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {removeButton}
      </Box>
      <Box
        sx={{
          gridArea: 'actionsBot',
          pt: `${LABEL_ROW_HEIGHT + LABEL_GAP_PX}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        }}
      >
        {autoSwitch('top')}
      </Box>
    </Box>
  );
}

export default InventoryAddRowEditor;
